// World cut boxes: oriented boxes (xz footprint + y band) inside which
// TERRAIN and BUILDING fragments are discarded. Used by the station zones to
// notch out the docking volume of the funicular cars at each terminal —
// nothing from the outside world may poke into a cabin a rider sits in.
// Client-only, computed from lifts.json terminals (no bake dependency).
import * as THREE from 'three';

export const MAX_CUTS = 6;
const _A = { value: Array.from({ length: MAX_CUTS }, () => new THREE.Vector4(0, 0, 1, 0)) };
const _B = { value: Array.from({ length: MAX_CUTS }, () => new THREE.Vector4(0, 0, 0, 0)) };
const _N = { value: 0 };
// world AABBs of the active cuts, [x0,x1,z0,z1,y0,y1], for terrain's static
// shader-variant test — a tile that meets no cut box compiles no cut discard
const _aabb = [];
let _gen = 0;

// boxes: [{x, z, ux, uz (unit axis), hl, hw, y0, y1}]
export function setWorldCuts(boxes) {
  const n = Math.min(MAX_CUTS, (boxes || []).length);
  _aabb.length = 0;
  for (let i = 0; i < n; i++) {
    const b = boxes[i];
    _A.value[i].set(b.x, b.z, b.ux, b.uz);
    _B.value[i].set(b.hl, b.hw, b.y0, b.y1);
    // half-extents of an oriented box projected onto the world axes
    const ex = Math.abs(b.ux) * b.hl + Math.abs(b.uz) * b.hw;
    const ez = Math.abs(b.uz) * b.hl + Math.abs(b.ux) * b.hw;
    _aabb.push([b.x - ex, b.x + ex, b.z - ez, b.z + ez, b.y0, b.y1]);
  }
  _N.value = n;
  _gen++;
}

export function cutBoxes() { return _aabb; }
// bumped whenever the cut set changes, so terrain can re-classify resident tiles
export function cutGen() { return _gen; }

export function cutUniforms(shader) {
  shader.uniforms.uCutA = _A;
  shader.uniforms.uCutB = _B;
  shader.uniforms.uCutN = _N;
}

export const CUT_DECL_GLSL = `uniform vec4 uCutA[${MAX_CUTS}];\n`
  + `uniform vec4 uCutB[${MAX_CUTS}];\nuniform int uCutN;\n`;

// world-position varying name is caller-supplied (terrain already has one)
export function cutGlsl(wp) {
  return `for (int ci = 0; ci < ${MAX_CUTS}; ci++) {
  if (ci >= uCutN) break;
  vec2 cd = ${wp}.xz - uCutA[ci].xy;
  float clx = cd.x * uCutA[ci].z + cd.y * uCutA[ci].w;
  float clz = -cd.x * uCutA[ci].w + cd.y * uCutA[ci].z;
  if (abs(clx) < uCutB[ci].x && abs(clz) < uCutB[ci].y
    && ${wp}.y > uCutB[ci].z && ${wp}.y < uCutB[ci].w) discard;
}
`;
}

// self-contained attach for materials without a world-pos varying (buildings).
// cut false: the world-pos varying only, no loop (userData.nocut, key
// '…-nc'): a building piece meeting no cut box (CUT-SPLIT, buildings.js) —
// the loop's discard cost the town's facades early-z (−10 ms of 23, desktop-igpu)
export function attachCut(mat, cut = true) {
  mat.userData.nocut = !cut;
  mat.onBeforeCompile = (shader) => {
    if (cut) cutUniforms(shader);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vCutW;')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvCutW = (modelMatrix * vec4(position, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vCutW;\n' + (cut ? CUT_DECL_GLSL : ''))
      .replace('#include <map_fragment>', (cut ? cutGlsl('vCutW') : '') + '#include <map_fragment>');
  };
  mat.customProgramCacheKey = () => 'worldcut' + (cut ? '' : '-nc');
}
